import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import AppsIcon from '@atlaskit/icon/core/apps';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { type OptionType } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { JiraAppSelectorIcon } from '@atlassian/search-common/jira-app-selector';
import { JiraAppSelectorIconWithGithub } from '@atlassian/search-common/jira-app-selector-with-github';

import {
	GITHUB_ICON_EXPERIMENT_DEFAULT,
	GITHUB_ICON_EXPERIMENT_NAME,
	GITHUB_ICON_EXPERIMENT_PARAM,
} from '../../../../../../common/constants/experiments';
import { ConfluenceAppSelectorIcon } from '../../../../../../common/constants/icons/confluence-app-selector';
import {
	is1pProductKey,
	is3pProductKey,
	isProductKey,
	PrimaryProductKeys,
	type ProductConfig,
	ProductKeys,
	useProductConfigs,
} from '../../../../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../../../../common/constants/schemas/3p-config';
import { useAppContext } from '../../../../../../common/ui/app-context';
import SelectFilter, {
	type SelectFilterOption,
} from '../../../../../../common/ui/filters/select-filter';
import {
	getProductOrder,
	getProductsOrderByPrimaryProduct,
} from '../../../../../../common/utils/product';
import { onAppFilterSelect as onAppFilterSelectEvent } from '../../../../../../common/utils/quick-find/events/app-filter-selected';
import { getWorkspaceNameFromProduct } from '../../../../../../common/utils/search-result-utils';
import { isUserOAuthed } from '../../../../../../controllers/oauth';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../../../controllers/quick-find/analytics';
import {
	useAvailableProducts,
	usePrimaryProduct,
	useThirdPartyConfigs,
} from '../../../../../../controllers/store/bootstrap';
import {
	useQuickFindActions,
	useQuickFindQuery,
} from '../../../../../../controllers/store/quick-find';
import { useSearchActions, useSelectedProduct } from '../../../../../../controllers/store/search';

import { i18n } from './messages';
import { shouldAppendGitHub } from './utils';

const MAX_SUGGESTED_1P = 3; // This includes the primary product
const MAX_SUGGESTED_3P = 2;

const appOptionStyles = cssMap({
	default: {
		width: '100%',
		display: 'grid',
		alignItems: 'center',
		gap: token('space.075'),
		gridTemplateColumns: 'auto 1fr auto',
	},
});

const getOptionLabel = (
	productKey: ProductKeys,
	productConfig: ProductConfig,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	// Edge case: This is to get the name of Confluence DC sites
	const workspaceName = getWorkspaceNameFromProduct(productKey, thirdPartyConfigs);
	if (workspaceName) {
		return workspaceName;
	}

	if (typeof productConfig.displayName === 'string') {
		return productConfig.displayName as string;
	}

	// In theory this we should not reach here as displayName is rendered as a string
	return '';
};

const AppSelector = ({
	isFilterInDropdown,
	fit,
}: {
	isFilterInDropdown?: boolean;
	fit?: 'default' | 'compact';
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const availableProducts = useAvailableProducts();
	const [selectedProduct] = useSelectedProduct();
	const productConfigs = useProductConfigs();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const { selectProductFilter, unselectProductFilter, clearAllFilters } = useSearchActions();
	const [primaryProduct] = usePrimaryProduct();
	const { onNavigate, generateSearchUrl, queryParams } = useAppContext();
	const quickFindQuery = useQuickFindQuery();
	const { setQuickFindOpen } = useQuickFindActions();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	const onAppSelectEvent = useCallback(
		(props: { productKey: ProductKeys; isRedirectedToFPS: boolean }) => {
			const { productKey, isRedirectedToFPS } = props;
			fireAnalyticsEvent(
				onAppFilterSelectEvent({
					actionSubjectId: productKey,
					action: 'selected',
					attributes: {
						...commonAttributes,
						isPostAuthAction: false,
						isRedirectedToFPS: isRedirectedToFPS,
						source: isFilterInDropdown ? 'app-selector-dropdown' : 'app-selector-inline',
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[commonAttributes, fireAnalyticsEvent, isFilterInDropdown, nonPrivacySafeAttributes],
	);

	const onSelectChange = useCallback(
		(value: SelectFilterOption | SelectFilterOption[]) => {
			const selectedValue = Array.isArray(value) ? value[1] : value;

			const productKey = selectedValue.value;
			if (!isProductKey(productKey)) {
				return;
			}

			if (productKey === primaryProduct) {
				selectProductFilter(productKey);
				onAppSelectEvent({ productKey, isRedirectedToFPS: false });
				return;
			}

			const onNavigateCallback =
				typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;

			onNavigateCallback(
				generateSearchUrl({
					...queryParams,
					product: productKey,
					text: quickFindQuery,
				}),
				'push',
			);

			onAppSelectEvent({ productKey, isRedirectedToFPS: true });
			clearAllFilters();
			setQuickFindOpen(false);
		},
		[
			clearAllFilters,
			generateSearchUrl,
			onAppSelectEvent,
			onNavigate,
			primaryProduct,
			queryParams,
			quickFindQuery,
			selectProductFilter,
			setQuickFindOpen,
		],
	);

	const allAppOptions = useMemo((): SelectFilterOption[] => {
		const productsOrder = getProductsOrderByPrimaryProduct(primaryProduct);
		const counts = { apps1P: 0, apps3P: 0 };
		const baseAppOptions = availableProducts
			.filter((productKey) => {
				// Always exclude Atlas
				if (productKey === ProductKeys.Atlas || productKey === ProductKeys.AtlassianWebCrawler) {
					return false;
				}

				return true;
			})
			.filter((productKey) => {
				const { userNeedsOAuth } = isUserOAuthed(productKey, thirdPartyConfigs);
				// For 3P products, undefined means not configured — filter out
				if (is3pProductKey(productKey)) {
					return userNeedsOAuth === false;
				}
				// For 1P products, undefined is expected (no OAuth needed)
				return !userNeedsOAuth;
			})
			.filter((productKey) => productConfigs[productKey])
			.sort((a, b) => getProductOrder(a, b, productsOrder))
			.filter((productKey) => {
				// Edge Case: Loom is considered as 1P only for the App selector
				if (productKey === ProductKeys.Loom) {
					if (counts.apps1P < MAX_SUGGESTED_1P) {
						counts.apps1P += 1;
						return true;
					}
					return false;
				}

				if (is1pProductKey(productKey) && counts.apps1P < MAX_SUGGESTED_1P) {
					counts.apps1P += 1;
					return true;
				}

				if (is3pProductKey(productKey) && counts.apps3P < MAX_SUGGESTED_3P) {
					counts.apps3P += 1;
					return true;
				}

				return false;
			})
			.map((productKey) => {
				return {
					value: productKey,
					isSelected: productKey === selectedProduct,
					label: getOptionLabel(productKey, productConfigs[productKey], thirdPartyConfigs),
					isDefault: false,
					trackingKey: productKey,
					queryParamValue: productKey,
					isSuggested: false,
				} satisfies SelectFilterOption;
			});

		if (
			shouldAppendGitHub({
				existingOptionValues: baseAppOptions.map((option) => option.value),
				primaryProduct,
				thirdPartyConfigs,
				hasGitHubConfig: Boolean(productConfigs[ProductKeys.Github]),
			})
		) {
			baseAppOptions.push({
				value: ProductKeys.Github,
				isSelected: ProductKeys.Github === selectedProduct,
				label: getOptionLabel(
					ProductKeys.Github,
					productConfigs[ProductKeys.Github],
					thirdPartyConfigs,
				),
				isDefault: false,
				trackingKey: ProductKeys.Github,
				queryParamValue: ProductKeys.Github,
				isSuggested: false,
			});
		}

		return baseAppOptions;
	}, [availableProducts, primaryProduct, productConfigs, selectedProduct, thirdPartyConfigs]);

	const selectedOption = useMemo((): SelectFilterOption[] => {
		const singleSelectedOption = allAppOptions.filter((option) => option.isSelected).at(0);
		return singleSelectedOption ? [singleSelectedOption] : [];
	}, [allAppOptions]);

	const selectedStateIcon = useMemo(() => {
		if (isProductKey(selectedProduct)) {
			const productConfig = productConfigs[selectedProduct];
			const Icon = productConfig.Icon;
			return <Icon size="small" />;
		}

		if (primaryProduct === PrimaryProductKeys.Confluence) {
			return <ConfluenceAppSelectorIcon />;
		}

		if (primaryProduct === PrimaryProductKeys.Jira) {
			const isGitHubConnected = !isUserOAuthed(ProductKeys.Github, thirdPartyConfigs ?? {})
				.userNeedsOAuth;
			const showGitHubIcon =
				isGitHubConnected &&
				FeatureGates.getExperimentValue<string>(
					GITHUB_ICON_EXPERIMENT_NAME,
					GITHUB_ICON_EXPERIMENT_PARAM,
					GITHUB_ICON_EXPERIMENT_DEFAULT,
				) === 'true';
			return showGitHubIcon ? <JiraAppSelectorIconWithGithub /> : <JiraAppSelectorIcon />;
		}

		return <AppsIcon label="" />;
	}, [primaryProduct, productConfigs, selectedProduct, thirdPartyConfigs]);

	const groupedOptions = [
		{
			label: formatMessage(i18n.appSelectLabelUnselected),
			options: allAppOptions,
		},
	];

	const formatOptionLabel = useCallback(
		(data: OptionType) => {
			const productKey = data.value;
			const appLabel = data.label;

			if (typeof productKey !== 'string' || !isProductKey(productKey)) {
				return <></>;
			}

			const productConfig = productConfigs[productKey];
			const Icon = productConfig.Icon;

			const AppOption = (props: { isRedirect: boolean }) => (
				<>
					<Icon size="small" />
					{appLabel}
					{props.isRedirect && (
						<LinkExternalIcon
							size="small"
							label="" // Label is within the tooltip
						/>
					)}
				</>
			);

			if (productKey === primaryProduct) {
				return (
					<Box xcss={appOptionStyles.default}>
						<AppOption isRedirect={false} />
					</Box>
				);
			}

			const tooltipContent = formatMessage(i18n.appRedirectTooltip, { appLabel });
			return (
				<Tooltip testId="qf-app-fps-redirect-tooltip" position="right" content={tooltipContent}>
					{(tooltipProps) => (
						<Box {...tooltipProps} xcss={appOptionStyles.default}>
							<AppOption isRedirect={true} />
						</Box>
					)}
				</Tooltip>
			);
		},
		[formatMessage, primaryProduct, productConfigs],
	);

	return (
		<SelectFilter
			contentFit={fit}
			testId="qf-app-selector"
			onSelectChange={onSelectChange}
			options={groupedOptions}
			selectedOptions={selectedOption}
			label={selectedOption.length === 0 ? formatMessage(i18n.app) : ''}
			placeholder={formatMessage(i18n.appSelectPlaceholder)}
			formatOptionLabel={formatOptionLabel}
			iconBefore={selectedStateIcon}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={true}
			canClearFilter
			onClearOptions={unselectProductFilter}
			hideOnHoverOut={
				primaryProduct === PrimaryProductKeys.Jira && fg('search_qf_close_dropdown_on_hover_off')
			}
		/>
	);
};

export { AppSelector };
